import {
    ApplicationRef,
    ComponentFactoryResolver,
    Injectable, Injector,
    Renderer2,
    RendererFactory2,
} from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { TrapFocusService } from '@client/app/modules/trap-focus/trap-focus.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import {
    DialogUpgradeWrapperComponent,
} from '@client/app/modules/dialog-upgrade-wrapper/dialog-upgrade-wrapper.component';
import { queryShadowElement } from '@client/app/utils/query-shadow-element.util';

export type DialogCloseValidationPayload = {
    dialogId: string;
    cb: () => boolean;
};

type Dialog = {
    id: string,
    dialog: any,
    overlay: any,
    cb: any,
    scope: any,
    focusTrap?: any,
};

@Injectable({
    providedIn: 'root',
})
export class MonDialog {
    dialogs: Map<string, Dialog> = new Map();
    doNotCallCallback = false;
    openCallBack: any = null;
    manualFocus = false;
    private renderer: Renderer2;

    constructor (
        private translateService: TranslateService,
        private trapFocusService: TrapFocusService,
        private rendererFactory: RendererFactory2,
        private monModalService: MonModalService,
        private appRef: ApplicationRef,
        private resolver: ComponentFactoryResolver,
        private injector: Injector,
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    init (cb: (options) => void) {
        this.openCallBack = cb;
    }

    callCb (options: any) {
        if (this.openCallBack) {
            this.openCallBack(options);
        }
    }

    create (options, callBack?, onBeforeClose?) {


        this.manualFocus = options.manualFocus || false;
        const body = document.body;
        const scope: any = {};
        const id = this.dialogId();

        if (typeof onBeforeClose === 'function') {
            scope.onBeforeClose = onBeforeClose;
        }

        // Detach the origin view for performance boost if cdRef is passed in
        if (options.cdRef) {
            options.cdRef.detach();
            scope.cdRef = options.cdRef;
        }

        scope.data = options.data || {};
        scope.size = options.size || 'md';
        scope.classes = options.classes || '';
        scope.zindex = 10000;
        scope.id = id;
        scope.close = close;
        scope.useStandardTemplate = options.useStandardTemplate || false;
        scope.setForm = this.setForm;
        scope.unsavedMsg = options.unsavedMsg || this.translateService.getString('Are you sure you want to close this form without saving your changes?');
        scope.unsavedTitle = options.unsavedTitle || this.translateService.getString('Unsaved changes');
        scope.manualFocus = options.manualFocus || false;
        const bodyComponent = options.body || null;
        const headerComponent = options.header || null;

        scope.body = bodyComponent;
        scope.header = headerComponent;

        let dialogContainer = body;
        if (!options.parentSelector) {
            options.parentSelector = '#common-dialog-wrapper';
            options.manualFocus = true;
        }

        dialogContainer = queryShadowElement(options.parentSelector, body);

        const overlay = this.createOverlay(dialogContainer, id, scope);
        const componentFactory = this.resolver.resolveComponentFactory(DialogUpgradeWrapperComponent);
        const compiledDialog = componentFactory.create(this.injector);

        for (const key of Object.keys(scope)) {
            compiledDialog.instance[key] = scope[key];
        }
        compiledDialog.instance.data = options.data || {};
        compiledDialog.instance.data.monDialogId = id;
        compiledDialog.instance.data.setForm = (dialogId, form) => {
            this.setForm(dialogId, form);
        };
        compiledDialog.instance.data.setCloseValidation = (payload: DialogCloseValidationPayload) => {
            this.setCloseValidation(payload);
        };

        this.appRef.attachView(compiledDialog.hostView);
        this.renderer.appendChild(dialogContainer, compiledDialog.location.nativeElement);
        this.addDialog(id, compiledDialog, overlay, callBack, scope);
        this.callCb(options);
        return id;
    }

    async close (id?: string, data?: any) {
        if (!id) {
            return;
        }
        const dialog = this.getDialog(id);

        if (dialog) {
            const scope = dialog.scope;
            let allowToClose = true;

            if (scope && typeof scope.onBeforeClose === 'function') {
                allowToClose = await scope.onBeforeClose();
            }

            if (allowToClose) {
                if (scope && scope.closeValidation && typeof scope.closeValidation === 'function') {
                    const canClose = scope.closeValidation();
                    if (!canClose) {
                        this.confirmClose(dialog, data);
                    } else {
                        this.closeDialog(dialog, data);
                    }
                    return;
                }

                if (dialog.scope.form && dialog.scope.form.dirty) {
                    this.confirmClose(dialog, data);
                } else {
                    this.closeDialog(dialog, data);
                }
            }

        }
    }

    closeDialog (dialog: Dialog, data) {
        if (!dialog) {
            return;
        }
        dialog.scope.cdRef?.reattach();
        const dialogNativeElement = dialog.dialog.location.nativeElement.querySelector('.mon-dialog');
        try {
            this.trapFocusService.remove(dialog.focusTrap);
        } catch (e) {}
        if (dialogNativeElement.classList) {
            dialogNativeElement.classList.add('mon-dialog-closeing');
            dialog.overlay.classList.add('mon-dialog-overlay-fadeout');
        }

        setTimeout(() => {
            if (dialogNativeElement.remove) {
                dialogNativeElement.remove();
                dialog.overlay.remove();
            } else if (dialogNativeElement.parentNode) {
                dialogNativeElement.parentNode.removeChild(dialogNativeElement);
                dialog.overlay.parentNode.removeChild(dialog.overlay);
            }
            if (dialog.cb && !this.doNotCallCallback) {
                dialog.cb(data);
            }
            dialog.dialog.destroy();
            this.dialogs.delete(dialog.id);
        }, 500);
    }

    confirmClose (dialog, data) {
        this.trapFocusService.pauseLast();
        this.monModalService.confirm({ message: dialog.scope.unsavedMsg, messageHTML: '', title: dialog.scope.unsavedTitle })
            .then(() => {
                this.closeDialog(dialog, data);
            }, () => {
                this.trapFocusService.unPauseLast();
            });
    }

    closeAll (callBack, dontCallCallback) {
        this.doNotCallCallback = Boolean(dontCallCallback);
        for (const id of this.dialogs.keys()) {
            this.close(id, undefined);
        }

        setTimeout(() => {
            if (callBack) {
                callBack();
            }
            this.doNotCallCallback = false;
        }, 500);
    }

    setFocus (id: string, focus) {
        if (id) {
            const dialog = this.getDialog(id);
            if (dialog) {
                dialog.focusTrap = focus;
            }
        }
    }

    setForm (payload, form?) {
        if (typeof payload !== 'string') {
            const { id: dialogId, form: dialogForm } = payload;
            const dialog = this.getDialog(dialogId);
            if (!dialog) {
                return;
            }

            if (!dialog.scope) {
                dialog.scope = { form: dialogForm };
            }
            dialog.scope.form = dialogForm;
            return;
        }

        const id = payload;
        if (id) {
            const dialog = this.getDialog(id);
            if (!dialog) {
                setTimeout(() => {
                    const d = this.getDialog(id);
                    if (d) {
                        if (!d.scope) {
                            d.scope = { form };
                        }
                        d.scope.form = form;
                    }
                });
            }

            if (dialog) {
                if (!dialog.scope) {
                    dialog.scope = { form };
                }
                dialog.scope.form = form;
            }
        }
    }

    setCloseValidation (payload: DialogCloseValidationPayload) {
        const { dialogId, cb } = payload;
        const dialog = this.getDialog(dialogId);
        if (!dialog) {
            return;
        }
        if (!dialog.scope) {
            dialog.scope = {};
        }
        dialog.scope.closeValidation = cb;
    }

    createOverlay (body, id, zindex) {
        const newEle = document.createElement('div');
        newEle.className = 'mon-dialog-overlay ' + id;
        newEle.style.setProperty('z-index', String(zindex));
        newEle.addEventListener('click', () => {
            this.close(id, undefined);
        });

        body.appendChild(newEle);
        return newEle;
    }

    addDialog (id: string, dialog, overlay, callBack, scope) {
        this.dialogs.set(id, { id: id, dialog: dialog, overlay: overlay, cb: callBack, scope: scope });
    }

    dialogId () {
        return 'mon-dialog-id-' + crypto.randomUUID();
    }

    getDialog (id: string) {
        if (!this.dialogs.has(id)) {
            return null;
        }
        return this.dialogs.get(id);
    }
}
